import { useState } from "react";
import { Pencil, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, streamGenerate } from "../lib/api";
import { CopyButton, Stamp } from "./Bits";

export const SECTIONS = [
  { key: "positioning", title: "Positioning", fields: [["hook", "The hook"], ["ideal_reader", "Ideal reader"], ["for_fans_of", "For fans of", "list"], ["tribe_tags", "Tribe tags", "tags"]] },
  { key: "keywords", title: "Keywords & categories", fields: [["kdp_keywords", "7 KDP backend keywords", "keywords"], ["categories", "Store categories", "list"]] },
  { key: "blurbs", title: "Blurbs", fields: [["blurb_short", "Short hook"], ["blurb_back", "Back cover"], ["blurb_long", "Long sales copy"]] },
  { key: "bio", title: "Author bio", fields: [["bio_short", "One line"], ["bio_medium", "Paragraph"], ["bio_long", "Full bio"]] },
  { key: "outreach", title: "Ad-free outreach", fields: [["pitch_bloggers", "Book bloggers & reviewers"], ["pitch_podcasts", "Podcasts"], ["pitch_bookstores", "Bookshops & libraries"]] },
];

const asText = (v) => (Array.isArray(v) ? v.join("\n") : v || "");

function FieldBlock({ book, setBook, field, label, kind }) {
  const value = book.kit?.[field];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const save = async () => {
    try {
      const { data } = await http.put(`/books/${book.id}/kit`, { field, value: kind ? draft.split("\n") : draft });
      setBook(data);
      setEditing(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="border-t border-[#E5DEC9] pt-5" data-testid={`kit-field-${field}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="label-mono">{label}</p>
        <div className="flex -mr-2.5">
          <CopyButton text={asText(value)} testid={`kit-copy-${field}-button`} />
          <button className="btn-ghost" onClick={() => { setDraft(asText(value)); setEditing(!editing); }} data-testid={`kit-edit-${field}-button`}><Pencil size={12} /> Edit</button>
        </div>
      </div>
      {editing ? (
        <div className="mt-3">
          <textarea rows={kind ? 6 : 8} className="input-paper" value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`kit-edit-${field}-textarea`} />
          {kind && <p className="text-xs text-[#8E8879] mt-1">One item per line.</p>}
          <div className="flex gap-3 mt-3">
            <button className="btn-primary !py-2" onClick={save} data-testid={`kit-save-${field}-button`}>Save</button>
            <button className="btn-ghost" onClick={() => setEditing(false)} data-testid={`kit-cancel-${field}-button`}>Cancel</button>
          </div>
        </div>
      ) : kind === "keywords" ? (
        <KeywordList book={book} setBook={setBook} />
      ) : kind === "tags" ? (
        <div className="flex flex-wrap gap-2.5 mt-3">{(value || []).map((t) => <Stamp key={t}>{t}</Stamp>)}</div>
      ) : kind === "list" ? (
        <ol className="mt-3 space-y-1.5">{(value || []).map((t, i) => <li key={i} className="flex gap-3"><span className="font-type text-xs text-[#C84B31] pt-1">{String(i + 1).padStart(2, "0")}</span><span>{t}</span></li>)}</ol>
      ) : (
        <p className={`mt-3 whitespace-pre-line leading-relaxed ${field === "hook" ? "font-display text-2xl" : ""}`}>{value}</p>
      )}
    </div>
  );
}

const CHECK = {
  confirmed: ["Readers search this", "bg-[#2E3A2F] text-white border-[#2E3A2F]"],
  unconfirmed: ["Not confirmed", "bg-[#FBF5E6] text-[#5C584E] border-[#8E8879]"],
  unchecked: ["Not checked", "bg-transparent text-[#8E8879] border-dashed border-[#8E8879]"],
};

function KeywordList({ book, setBook }) {
  const [busy, setBusy] = useState(false);
  const words = book.kit?.kdp_keywords || [];
  const checks = book.kit?.keyword_checks || {};
  const call = async (fn) => {
    setBusy(true);
    try {
      setBook((await fn()).data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const swap = (i, s) => call(() => http.put(`/books/${book.id}/kit`, { field: "kdp_keywords", value: words.map((w, j) => (j === i ? s : w)) }));
  return (
    <div className="mt-3" data-testid="keyword-list">
      <ol className="space-y-4">
        {words.map((w, i) => {
          const c = checks[w] || { status: "unchecked", suggestions: [] };
          const [label, cls] = CHECK[c.status] || CHECK.unchecked;
          return (
            <li key={i} className="grid grid-cols-[2rem_1fr] gap-2" data-testid={`keyword-row-${i}`}>
              <span className="font-type text-xs text-[#C84B31] pt-1">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <span>{w}</span>
                  <span className={`font-type text-[0.6rem] uppercase tracking-wider px-2 py-0.5 border ${cls}`} data-testid={`keyword-status-${i}`}>{label}</span>
                </div>
                {c.suggestions?.length > 0 && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                    <span className="font-type text-[0.6rem] uppercase text-[#8E8879] pt-0.5">Amazon searches:</span>
                    {c.suggestions.map((s, j) => (
                      <button key={s} disabled={busy} onClick={() => swap(i, s)} title="Use this keyword" data-testid={`keyword-${i}-swap-${j}`}
                        className="text-sm text-[#5C584E] border-b border-dotted border-[#8E8879] hover:text-[#C84B31] hover:border-[#C84B31] disabled:opacity-40 transition-colors">⇄ {s}</button>
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      <button className="btn-ghost mt-4 -ml-2.5" disabled={busy || !words.length} onClick={() => call(() => http.post(`/books/${book.id}/keywords/check`))} data-testid="keywords-recheck-button">
        <RefreshCw size={12} /> {busy ? "Checking Amazon…" : "Check on Amazon again"}
      </button>
      <p className="text-xs text-[#8E8879] mt-1">Checked against Amazon.com (US) search suggestions.</p>
    </div>
  );
}

function Section({ s, i, book, setBook, live, onGenerate, busy }) {
  const has = s.fields.some(([k]) => book.kit?.[k]?.length);
  return (
    <section className="grid lg:grid-cols-12 gap-6 lg:gap-12 py-12 border-t border-[#121210]" data-testid={`kit-section-${s.key}`}>
      <div className="lg:col-span-4">
        <p className="font-type text-sm text-[#C84B31]">{String(i + 1).padStart(2, "0")}</p>
        <h2 className="font-display text-3xl mt-2">{s.title}</h2>
        <button className="btn-secondary mt-5" onClick={() => onGenerate(s.key)} disabled={busy} data-testid={`kit-generate-${s.key}-button`}>
          {has ? <><RefreshCw size={12} /> Try another version</> : <><Sparkles size={12} /> Create section</>}
        </button>
      </div>
      <div className="lg:col-span-8 space-y-7">
        {live !== null ? (
          <pre className="font-type text-[0.8rem] leading-relaxed whitespace-pre-wrap bg-[#F6F2EA] border border-[#E5DEC9] p-6 caret max-h-[28rem] overflow-y-auto" data-testid={`kit-stream-${s.key}`}>{live || "Preparing…"}</pre>
        ) : has ? (
          s.fields.map(([k, label, kind]) => <FieldBlock key={k} book={book} setBook={setBook} field={k} label={label} kind={kind} />)
        ) : (
          <p className="text-[#8E8879] italic font-display text-xl pt-2">Not written yet.</p>
        )}
      </div>
    </section>
  );
}

export default function LaunchKit({ book, setBook }) {
  const [active, setActive] = useState(null);
  const [live, setLive] = useState("");
  const busy = active !== null;

  const run = async (key) => {
    setActive(key);
    setLive("");
    try {
      const updated = await streamGenerate(book.id, key, {}, (d) => setLive((p) => p + d));
      setBook(updated);
      return true;
    } catch (e) {
      toast.error(e.message);
      return false;
    } finally {
      setActive(null);
    }
  };

  const runAll = async () => {
    for (const s of SECTIONS) {
      if (!(await run(s.key))) break;
    }
  };

  return (
    <div data-testid="launch-kit">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-10">
        <div className="max-w-xl">
          <h2 className="font-display text-3xl sm:text-4xl">Your launch kit</h2>
          <p className="mt-3 text-[#5C584E]">Everything your book needs to reach readers. Edit, copy or try another version of any part.</p>
        </div>
        <button className="btn-primary" onClick={runAll} disabled={busy} data-testid="kit-generate-all-button">
          <Sparkles size={14} /> {busy ? "Creating…" : "Create my launch kit"}
        </button>
      </div>
      {SECTIONS.map((s, i) => (
        <Section key={s.key} s={s} i={i} book={book} setBook={setBook} live={active === s.key ? live : null} onGenerate={run} busy={busy} />
      ))}
    </div>
  );
}
